import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { api, ADMIN_TOKEN_KEY } from "@/lib/api";

const AdminAuthContext = createContext(null);

// A token is only "valid" if it exists AND hasn't expired (decode the JWT exp).
function tokenValid(token) {
  if (!token) return false;
  try {
    let b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const payload = JSON.parse(atob(b64));
    if (payload.exp && payload.exp * 1000 <= Date.now()) return false;
    return true;
  } catch {
    return false;
  }
}

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY));

  const login = useCallback(async (password) => {
    const { data } = await api.post("/admin/login", { password });
    localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
    setToken(data.token);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken(null);
  }, []);

  // Clean up an expired/invalid token so we don't sit "logged in" but broken.
  useEffect(() => {
    if (token && !tokenValid(token)) {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      setToken(null);
    }
  }, [token]);

  return (
    <AdminAuthContext.Provider value={{ token, isAuthed: tokenValid(token), login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
